import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api, setToken, getToken } from "@/lib/api";
import {
    Shield, Users, Radio, Activity, Bell, TrendingUp, TrendingDown,
    Eye, AlertCircle, ChevronLeft, Loader2, RefreshCw, Phone, Key, Lock,
    RotateCw, LogOut, Gem, Package, Target, Zap,
} from "lucide-react";
import { toast } from "sonner";

const tg = typeof window !== "undefined" ? window.Telegram?.WebApp : null;

/* ━━━━ Telegram WebApp Auth ━━━━ */
function useTelegramAuth() {
    const [authed, setAuthed] = useState(false);
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!tg) { setError("not_telegram"); setLoading(false); return; }
        tg.ready();
        tg.expand();
        try { tg.setHeaderColor("#0a0e14"); tg.setBackgroundColor("#0a0e14"); } catch {}
        // Hide Emergent badge inside Mini App
        const badge = document.getElementById("emergent-badge");
        if (badge) badge.style.display = "none";
        // Remove any overlays blocking touch
        document.querySelectorAll('[style*="z-index: 9999"], [style*="z-index:9999"]').forEach(el => el.style.display = "none");

        const initData = tg.initData;
        if (!initData) { setError("no_init_data"); setLoading(false); return; }

        (async () => {
            try {
                const resp = await api.post("/auth/webapp-auth", { init_data: initData });
                setToken(resp.data.access_token);
                setUser(resp.data.user);
                setAuthed(true);
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) setError("not_allowed");
                else if (status === 401) setError("invalid_data");
                else setError("auth_failed");
            }
            setLoading(false);
        })();
    }, []);

    return { authed, user, error, loading };
}

/* ━━━━ Phone Login Flow ━━━━ */
function PhoneLogin({ telegramUserId, onSuccess }) {
    const [step, setStep] = useState("phone"); // phone | code | twofa
    const [phone, setPhone] = useState("");
    const [proxyUrl, setProxyUrl] = useState("");
    const [showProxyHelp, setShowProxyHelp] = useState(false);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [phoneCodeHash, setPhoneCodeHash] = useState("");
    const [viaProxy, setViaProxy] = useState(false);

    const sendCode = async (e) => {
        e.preventDefault();
        if (!phone.trim()) return;
        setLoading(true);
        try {
            const resp = await api.post("/auth/mtproto/send-code", {
                phone: phone.trim(),
                telegram_user_id: telegramUserId,
                proxy_url: proxyUrl.trim() || null,
            });
            setPhoneCodeHash(resp.data.phone_code_hash);
            setViaProxy(!!resp.data.via_proxy);
            setStep("code");
            toast.success(resp.data.via_proxy ? "تم إرسال الرمز عبر البروكسي" : "تم إرسال الرمز");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "فشل إرسال الرمز");
        }
        setLoading(false);
    };

    const verifyCode = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setLoading(true);
        try {
            const resp = await api.post("/auth/mtproto/verify-code", {
                phone: phone.trim(),
                code: code.trim(),
                telegram_user_id: telegramUserId,
            });
            if (resp.data.needs_2fa) {
                setStep("twofa");
                toast.info("أدخل كلمة مرور التحقق بخطوتين");
            } else {
                setToken(resp.data.access_token);
                onSuccess();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "الرمز غير صحيح");
        }
        setLoading(false);
    };

    const verify2FA = async (e) => {
        e.preventDefault();
        if (!password.trim()) return;
        setLoading(true);
        try {
            const resp = await api.post("/auth/mtproto/verify-2fa", {
                phone: phone.trim(),
                password: password.trim(),
                telegram_user_id: telegramUserId,
            });
            setToken(resp.data.access_token);
            onSuccess();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "كلمة المرور غير صحيحة");
        }
        setLoading(false);
    };

    return (
        <div className="p-4 space-y-4">
            <div className="text-center py-4">
                <Shield className="h-10 w-10 mx-auto text-cyan-400 mb-2" />
                <h1 className="text-xl font-bold">Guardian Pro</h1>
                <p className="text-sm text-muted-foreground mt-1">ربط حسابك لتفعيل تبديل المعرّف</p>
            </div>

            {step === "phone" && (
                <Card className="bg-card/60 border-border/40">
                    <CardContent className="p-4">
                        <form onSubmit={sendCode} className="space-y-3">
                            <div>
                                <label className="text-sm text-muted-foreground flex items-center gap-2 mb-1.5">
                                    <Phone className="h-3.5 w-3.5" /> رقم الهاتف (مع رمز الدولة)
                                </label>
                                <Input
                                    type="tel"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    placeholder="+963912345678"
                                    dir="ltr"
                                    className="text-lg"
                                    required
                                />
                            </div>

                            {/* MTProto Proxy (optional but recommended) */}
                            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                                <button
                                    type="button"
                                    onClick={() => setShowProxyHelp(!showProxyHelp)}
                                    className="w-full text-right flex items-center justify-between text-sm font-medium text-amber-300"
                                >
                                    <span className="flex items-center gap-2">
                                        🛡️ بروكسي MTProto (موصى به)
                                    </span>
                                    <span className="text-xs">{showProxyHelp ? "▲" : "▼"}</span>
                                </button>

                                {showProxyHelp && (
                                    <div className="mt-2 text-[11px] text-amber-200/80 leading-relaxed space-y-2">
                                        <p>السيرفر في فرنسا 🇫🇷 — إذا حسابك معتاد على بلد آخر، قد يرى تلغرام هذا "تسجيل دخول مشبوه".</p>

                                        <div className="bg-rose-500/10 border border-rose-500/30 rounded p-2 text-rose-200">
                                            ⚠️ <b>لا تستخدم</b> بروكسيات MTProto العامة (مثل <span className="font-mono" dir="ltr">@MTProxybot</span> أو قنوات البروكسي المجانية) — معظمها مرصودة عند تلغرام كمصادر spam وستؤدي لحظر فوري!
                                        </div>

                                        <p><b>✅ الخيارات الآمنة (مرتّبة من الأفضل):</b></p>
                                        <ol className="list-decimal pr-4 space-y-1">
                                            <li><b>VPS خاص بك في بلدك</b> (~$4/شهر من Hetzner/Contabo/DigitalOcean) → ركّب <span className="font-mono" dir="ltr">3proxy</span> أو <span className="font-mono" dir="ltr">dante-server</span> SOCKS5</li>
                                            <li><b>بروكسي SOCKS5 سكني</b> (residential) من خدمة موثوقة</li>
                                            <li><b>WireGuard/OpenVPN</b> من جهازك إلى السيرفر</li>
                                            <li>بدون بروكسي — اقبل المخاطرة (مقبول لاستخدام قليل)</li>
                                        </ol>

                                        <p className="mt-2"><b>الصيغ المدعومة:</b></p>
                                        <code className="block text-[10px] bg-black/30 p-1.5 rounded" dir="ltr">socks5://user:pass@host:port</code>
                                        <code className="block text-[10px] bg-black/30 p-1.5 rounded" dir="ltr">socks5://host:port</code>
                                        <code className="block text-[10px] bg-black/30 p-1.5 rounded" dir="ltr">http://user:pass@host:port</code>
                                        <code className="block text-[10px] bg-black/30 p-1.5 rounded opacity-60" dir="ltr">MTProto (only if you own it!)</code>
                                    </div>
                                )}

                                <Input
                                    type="text"
                                    value={proxyUrl}
                                    onChange={e => setProxyUrl(e.target.value)}
                                    placeholder="socks5://user:pass@host:port (اختياري)"
                                    dir="ltr"
                                    className="mt-2 text-xs bg-black/20 border-amber-500/20 placeholder:text-[10px]"
                                />
                            </div>

                            <Button type="submit" disabled={loading} className="w-full gap-2">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                                إرسال رمز التحقق
                            </Button>
                        </form>
                        <p className="text-[11px] text-muted-foreground mt-3 text-center">
                            سيصلك رمز تحقق على تلغرام. نستخدم حسابك فقط لتبديل المعرّف عند الهجمات.
                        </p>
                    </CardContent>
                </Card>
            )}

            {step === "code" && (
                <Card className="bg-card/60 border-border/40">
                    <CardContent className="p-4">
                        {viaProxy && (
                            <div className="mb-3 text-[11px] text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 rounded p-2 text-center">
                                🛡️ متصل عبر البروكسي — الـ IP الظاهر هو IP البروكسي
                            </div>
                        )}
                        <form onSubmit={verifyCode} className="space-y-3">
                            <div>
                                <label className="text-sm text-muted-foreground flex items-center gap-2 mb-1.5">
                                    <Key className="h-3.5 w-3.5" /> رمز التحقق
                                </label>
                                <Input
                                    type="text"
                                    value={code}
                                    onChange={e => setCode(e.target.value)}
                                    placeholder="12345"
                                    dir="ltr"
                                    className="text-2xl text-center tracking-[0.5em]"
                                    maxLength={6}
                                    required
                                    autoFocus
                                />
                            </div>
                            <Button type="submit" disabled={loading} className="w-full gap-2">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                                تأكيد الرمز
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}

            {step === "twofa" && (
                <Card className="bg-card/60 border-border/40">
                    <CardContent className="p-4">
                        <form onSubmit={verify2FA} className="space-y-3">
                            <div>
                                <label className="text-sm text-muted-foreground flex items-center gap-2 mb-1.5">
                                    <Lock className="h-3.5 w-3.5" /> كلمة مرور التحقق بخطوتين (2FA)
                                </label>
                                <Input
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    autoFocus
                                />
                            </div>
                            <Button type="submit" disabled={loading} className="w-full gap-2">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                                تأكيد
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

/* ━━━━ Mini KPI ━━━━ */
function MiniKpi({ label, value, icon: Icon, color = "cyan" }) {
    const c = { cyan: "ring-cyan-500/15 text-cyan-400", emerald: "ring-emerald-500/15 text-emerald-400", amber: "ring-amber-500/15 text-amber-400", rose: "ring-rose-500/15 text-rose-400" };
    return (
        <Card className={`bg-card/60 ring-1 ${c[color] || c.cyan}`}>
            <CardContent className="p-3 flex items-center justify-between">
                <div><p className="text-[10px] text-muted-foreground">{label}</p><p className="text-lg font-bold" dir="ltr">{(value || 0).toLocaleString()}</p></div>
                {Icon && <Icon className={`h-5 w-5 opacity-50`} />}
            </CardContent>
        </Card>
    );
}

/* ━━━━ Username Rotation Dialog ━━━━ */
function RotateDialog({ channel, onDone }) {
    const [newUsername, setNewUsername] = useState("");
    const [loading, setLoading] = useState(false);

    const rotate = async (auto) => {
        setLoading(true);
        try {
            const resp = await api.post("/user/rotate-username", {
                chat_id: channel.chat_id,
                new_username: auto ? "" : newUsername.trim(),
            });
            toast.success(`تم التبديل: @${resp.data.new_username}`);
            onDone(resp.data.new_username);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "فشل التبديل");
        }
        setLoading(false);
    };

    return (
        <Card className="bg-card/60 border-border/40">
            <CardHeader className="pb-2 pt-3 px-3">
                <CardTitle className="text-sm flex items-center gap-2"><RotateCw className="h-4 w-4 text-cyan-400" /> تبديل المعرّف</CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-3">
                <p className="text-xs text-muted-foreground">المعرّف الحالي: <code dir="ltr">@{channel.username || "بدون"}</code></p>
                <Button onClick={() => rotate(true)} disabled={loading} variant="destructive" className="w-full gap-2">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
                    تبديل تلقائي (عشوائي)
                </Button>
                <div className="flex gap-2">
                    <Input value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="معرّف مخصص" dir="ltr" className="text-sm" />
                    <Button onClick={() => rotate(false)} disabled={loading || !newUsername.trim()} size="sm">تبديل</Button>
                </div>
                <p className="text-[10px] text-muted-foreground">بعد انتهاء الهجوم استخدم "استعادة" لإرجاع المعرّف الأصلي</p>
            </CardContent>
        </Card>
    );
}

/* ━━━━ Premium Intelligence (batches + active audience) ━━━━ */
function PremiumIntel() {
    const [data, setData] = useState(null);
    const [batches, setBatches] = useState(null);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("batches"); // batches | active
    const fmt = (n) => (n ?? 0).toLocaleString("en-US");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, b, a] = await Promise.all([
                api.get("/stats/premium-overview"),
                api.get("/stats/premium-batches"),
                api.get("/stats/active-audience"),
            ]);
            setData(o.data); setBatches(b.data); setActive(a.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "تعذّر تحميل بيانات البريميوم");
        }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-4 space-y-3"><Skeleton className="h-24" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div>;

    const t = data?.totals || {};
    const bs = batches?.summary || data?.batches_summary || {};
    const upd = data?.update || {};

    return (
        <div className="p-3 space-y-3 pb-20">
            <div className="text-center py-1">
                <Gem className="h-7 w-7 mx-auto text-amber-400 mb-1" />
                <h2 className="text-base font-bold">ذكاء البريميوم</h2>
                {upd.status && <p className="text-[11px] text-muted-foreground mt-0.5">{upd.status}</p>}
            </div>

            {/* update prediction */}
            {upd.last && (
                <Card className="bg-amber-500/5 border-amber-500/20">
                    <CardContent className="p-3 text-[11px] text-amber-200 space-y-1">
                        <div className="flex items-center justify-between">
                            <span>آخر تحديث ترتيب</span>
                            <b dir="ltr">{upd.last} (منذ {upd.days_since_last} يوم)</b>
                        </div>
                        <div className="flex items-center justify-between">
                            <span>التحديث القادم المتوقّع</span>
                            <b dir="ltr">{upd.predict_median}</b>
                        </div>
                        <div className="flex items-center justify-between text-muted-foreground">
                            <span>متوسط الدورة</span><span dir="ltr">~{upd.median_gap} يوم (أقصى {upd.max_gap})</span>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-2">
                <MiniKpi label="محتسب (Boost) 💎" value={t.counted} icon={Gem} color="amber" />
                <MiniKpi label="حاضر فعلاً (متتبَّع)" value={t.present_premium} icon={Users} color="cyan" />
                <MiniKpi label="دفعات بانتظار التحديث" value={bs.pending_premium} icon={Package} color="amber" />
                <MiniKpi label="متوسط تطابق الفرضية %" value={active?.summary?.avg_hypothesis_match} icon={Target} color="emerald" />
            </div>

            {/* tab switch */}
            <div className="flex gap-2">
                <button type="button" onClick={() => setTab("batches")} className={`flex-1 rounded-lg py-2 text-sm font-medium border transition-colors touch-manipulation select-none ${tab === "batches" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-card/60 border-border/30 text-muted-foreground"}`} style={{ WebkitTapHighlightColor: 'transparent' }}>
                    <Package className="h-4 w-4 inline mr-1" /> الدفعات
                </button>
                <button type="button" onClick={() => setTab("active")} className={`flex-1 rounded-lg py-2 text-sm font-medium border transition-colors touch-manipulation select-none ${tab === "active" ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "bg-card/60 border-border/30 text-muted-foreground"}`} style={{ WebkitTapHighlightColor: 'transparent' }}>
                    <Eye className="h-4 w-4 inline mr-1" /> الجمهور النشط
                </button>
            </div>

            {tab === "batches" && (
                <Card className="bg-card/60 border-border/40">
                    <CardHeader className="pb-2 pt-3 px-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Package className="h-4 w-4 text-amber-400" /> دفعات شراء المميزين
                            <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0 border-amber-500/20 text-amber-300" dir="ltr">
                                {bs.measured ?? 0}/{bs.total_batches ?? 0}
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 space-y-2">
                        <p className="text-[10px] text-muted-foreground">
                            «نسبة التفعيل» = كم من أعضاء الدفعة احتسبهم تلغرام بعد تحديث الترتيب.
                            {bs.avg_activation_pct != null && <> متوسط التفعيل: <b className="text-amber-300">{bs.avg_activation_pct}%</b>.</>}
                        </p>
                        {(!batches?.batches || batches.batches.length === 0) && (
                            <p className="text-[11px] text-muted-foreground text-center py-3">لا توجد دفعات مكتشفة بعد.</p>
                        )}
                        {(batches?.batches || []).slice(0, 40).map((b, i) => (
                            <div key={i} className="rounded-lg bg-card/80 border border-border/30 p-2.5">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium truncate">{b.title}</p>
                                    <span className="text-[10px] text-muted-foreground font-mono shrink-0" dir="ltr">{(b.start || "").slice(0, 10)}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px]">
                                    <Badge variant="outline" className="px-1.5 py-0 border-amber-500/20 text-amber-300" dir="ltr">
                                        🛒 {fmt(b.size_premium)}
                                    </Badge>
                                    {b.present_premium != null && (
                                        <Badge variant="outline" className="px-1.5 py-0 border-cyan-500/20 text-cyan-300" dir="ltr">
                                            ✓ متبقّي {fmt(b.present_premium)}
                                        </Badge>
                                    )}
                                    {b.left_premium ? (
                                        <span className="text-rose-300" dir="ltr">↓ غادر {fmt(b.left_premium)}</span>
                                    ) : null}
                                    <span className="text-muted-foreground">رابط {Math.round((b.invite_ratio || 0) * 100)}%</span>
                                    {b.status === "measured" ? (
                                        <Badge variant="outline" className="px-1.5 py-0 border-emerald-500/30 text-emerald-300" dir="ltr">
                                            ✅ تفعيل {b.activation_pct}% ({fmt(b.counted_delta)})
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="px-1.5 py-0 border-border/40 text-muted-foreground">⏳ بانتظار التحديث</Badge>
                                    )}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {tab === "active" && (
                <Card className="bg-card/60 border-border/40">
                    <CardHeader className="pb-2 pt-3 px-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Eye className="h-4 w-4 text-cyan-400" /> الجمهور النشط واختبار الفرضية
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 space-y-2">
                        <p className="text-[10px] text-muted-foreground">
                            الفرضية: تلغرام يحتسب فقط المميزين ضمن «الجمهور النشط». المتوقّع = حاضر × نسبة النشاط.
                            {active?.summary?.avg_hypothesis_match != null && <> متوسط التطابق: <b className="text-emerald-300">{active.summary.avg_hypothesis_match}%</b>.</>}
                        </p>
                        {(!active?.channels || active.channels.length === 0) && (
                            <p className="text-[11px] text-muted-foreground text-center py-3">لا توجد بيانات جمهور نشط بعد.</p>
                        )}
                        {(active?.channels || []).slice(0, 40).map((c, i) => (
                            <div key={i} className="rounded-lg bg-card/80 border border-border/30 p-2.5">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-medium truncate">{c.title}</p>
                                    {c.hypothesis_match != null && (
                                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${c.hypothesis_match >= 70 ? "border-emerald-500/30 text-emerald-300" : "border-amber-500/30 text-amber-300"}`} dir="ltr">
                                            تطابق {c.hypothesis_match}%
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-muted-foreground" dir="ltr">
                                    <span className="text-cyan-300">👁 {fmt(c.active_audience)}</span>
                                    {c.active_ratio != null && <span>({c.active_ratio}%)</span>}
                                    {c.counted != null && <span>· محتسب 💎 {fmt(c.counted)}</span>}
                                    {c.expected_counted != null && <span>· متوقّع {fmt(c.expected_counted)}</span>}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

/* ━━━━ Mini Dashboard ━━━━ */
function MiniDashboard({ user, onShowConnect, onDisconnect }) {
    const [stats, setStats] = useState(null);
    const [channels, setChannels] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [view, setView] = useState("home");
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [busyAccount, setBusyAccount] = useState(null);

    const hasSession = accounts.length > 0;
    const accountByChannel = (ch) => accounts.find(a => a.account_user_id === ch.admin_account_user_id);

    const loadHome = useCallback(async () => {
        setLoading(true);
        try {
            const [o, ch, al, ev, acc] = await Promise.all([
                api.get("/stats/overview"),
                api.get("/user/my-channels"),
                api.get("/alerts?only_unacked=true"),
                api.get("/events?limit=20"),
                api.get("/auth/mtproto/accounts"),
            ]);
            setStats(o.data);
            setChannels(ch.data.channels || []);
            setAlerts(al.data.alerts || []);
            setEvents(ev.data.events || []);
            setAccounts(acc.data.accounts || []);
        } catch {}
        setLoading(false);
    }, []);

    const disconnectAccount = async (accountUserId, username) => {
        if (!window.confirm(`فصل الحساب @${username}؟ ستحتاج إعادة ربطه لاحقًا.`)) return;
        setBusyAccount(accountUserId);
        try {
            await api.delete(`/auth/mtproto/accounts/${accountUserId}`);
            toast.success("تم فصل الحساب");
            await loadHome();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "فشل الفصل");
        }
        setBusyAccount(null);
    };

    const refreshChannels = async (accountUserId) => {
        setBusyAccount(accountUserId);
        try {
            const r = await api.post(`/auth/mtproto/accounts/${accountUserId}/refresh-channels`);
            toast.success(`تم تحديث القنوات (${r.data.channels_count})`);
            await loadHome();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "فشل التحديث");
        }
        setBusyAccount(null);
    };

    useEffect(() => { loadHome(); }, [loadHome]);

    useEffect(() => {
        if (!tg) return;
        if (view !== "home") {
            tg.BackButton.show();
            const back = () => { setView("home"); setSelectedChannel(null); };
            tg.BackButton.onClick(back);
            return () => { tg.BackButton.offClick(back); };
        } else { tg.BackButton.hide(); }
    }, [view]);

    if (loading) return <div className="p-4 space-y-3"><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" /></div>;

    if (view === "premium") return <PremiumIntel />;

    if (view === "channel" && selectedChannel) {
        return (
            <div className="p-3 space-y-3 pb-20">
                <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl border ${selectedChannel.chat_type === "channel" ? "bg-cyan-500/10 border-cyan-500/20" : "bg-violet-500/10 border-violet-500/20"}`}>
                        {selectedChannel.chat_type === "channel" ? <Radio className="h-5 w-5 text-cyan-400" /> : <Users className="h-5 w-5 text-violet-400" />}
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-base font-bold truncate">{selectedChannel.title}</h2>
                        <p className="text-xs text-muted-foreground" dir="ltr">{selectedChannel.username ? `@${selectedChannel.username}` : selectedChannel.chat_id}</p>
                    </div>
                </div>

                {(() => {
                    const adminAcc = accountByChannel(selectedChannel);
                    if (adminAcc) {
                        return (
                            <>
                                <Card className="bg-emerald-500/5 border-emerald-500/20">
                                    <CardContent className="p-2.5 text-[11px] text-emerald-300 flex items-center gap-2">
                                        <Shield className="h-3.5 w-3.5 shrink-0" />
                                        ستُنفَّذ التغييرات عبر: <b className="font-mono" dir="ltr">@{adminAcc.telegram_username || adminAcc.telegram_name}</b>
                                    </CardContent>
                                </Card>
                                <RotateDialog channel={selectedChannel} onDone={(u) => { setSelectedChannel({...selectedChannel, username: u}); loadHome(); }} />
                            </>
                        );
                    }
                    return (
                        <Card className="bg-amber-500/5 border-amber-500/20">
                            <CardContent className="p-3 text-xs text-amber-300 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 shrink-0" />
                                لا يوجد حساب مالك مربوط لهذه القناة — اربط الحساب المالك أولًا لتفعيل تبديل المعرّف
                            </CardContent>
                        </Card>
                    );
                })()}

                <Card className="bg-card/60 border-border/40">
                    <CardHeader className="pb-2 pt-3 px-3"><CardTitle className="text-sm">الإحصائيات</CardTitle></CardHeader>
                    <CardContent className="px-3 pb-3">
                        <div className="grid grid-cols-2 gap-2">
                            <MiniKpi label="الأعضاء" value={selectedChannel.member_count} icon={Users} color="cyan" />
                            <MiniKpi label="انضمام اليوم" value={selectedChannel.joins_today} icon={TrendingUp} color="emerald" />
                            <MiniKpi label="مغادرة اليوم" value={selectedChannel.leaves_today} icon={TrendingDown} color="rose" />
                            <MiniKpi label="الصافي" value={selectedChannel.net_today} icon={Activity} color={selectedChannel.net_today >= 0 ? "emerald" : "rose"} />
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-3 space-y-3 pb-20">
            <div className="relative text-center py-2">
                <Shield className="h-8 w-8 mx-auto text-cyan-400 mb-1" />
                <h1 className="text-lg font-bold">Guardian Pro</h1>
                <p className="text-xs text-muted-foreground">مرحباً {user?.first_name || user?.username}</p>
            </div>

            {/* Accounts card — multi-account connection management */}
            <Card className="bg-card/60 border-border/40">
                <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Shield className="h-4 w-4 text-cyan-400" />
                        حسابات مالك القنوات
                        <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0 border-cyan-500/20 text-cyan-300" dir="ltr">{accounts.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 space-y-2">
                    {accounts.length === 0 && (
                        <p className="text-[11px] text-muted-foreground text-center py-2">
                            لم تربط أي حساب بعد. اضغط الزر أسفل لإضافة أوّل حساب.
                        </p>
                    )}
                    {accounts.map(acc => (
                        <div key={acc.account_user_id} className="flex items-center gap-2 rounded-lg bg-card/80 border border-border/30 p-2.5">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-medium truncate">{acc.telegram_name || acc.telegram_username}</p>
                                    {acc.is_primary && <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-500/30 text-emerald-300">أساسي</Badge>}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                                    {acc.telegram_username && <span className="font-mono" dir="ltr">@{acc.telegram_username}</span>}
                                    <span>•</span>
                                    <span><b className="text-cyan-300" dir="ltr">{acc.channels_count}</b> قناة</span>
                                </div>
                            </div>
                            <Button
                                variant="ghost" size="sm" disabled={busyAccount === acc.account_user_id}
                                onClick={() => refreshChannels(acc.account_user_id)}
                                className="h-7 w-7 p-0 text-cyan-300 hover:bg-cyan-500/10"
                                title="تحديث القنوات"
                            >
                                {busyAccount === acc.account_user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                            </Button>
                            <Button
                                variant="ghost" size="sm" disabled={busyAccount === acc.account_user_id}
                                onClick={() => disconnectAccount(acc.account_user_id, acc.telegram_username || acc.telegram_name)}
                                className="h-7 w-7 p-0 text-rose-300 hover:bg-rose-500/10"
                                title="فصل الحساب"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={onShowConnect}
                        className="w-full flex items-center justify-center gap-2 text-sm font-medium text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 active:bg-cyan-500/30 border border-cyan-500/30 rounded-lg py-2.5 transition-colors touch-manipulation select-none"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                        <Phone className="h-4 w-4" />
                        {accounts.length === 0 ? "اربط حسابك الأوّل" : "➕ إضافة حساب آخر"}
                    </button>
                </CardContent>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 gap-2">
                <MiniKpi label="القنوات" value={stats?.total_groups} icon={Radio} color="cyan" />
                <MiniKpi label="تنبيهات" value={stats?.pending_alerts} icon={Bell} color={stats?.pending_alerts > 0 ? "rose" : "emerald"} />
                <MiniKpi label="أحداث اليوم" value={stats?.today_events} icon={Activity} color="cyan" />
                <MiniKpi label="هجمات" value={stats?.today_raids} icon={AlertCircle} color={stats?.today_raids > 0 ? "rose" : "emerald"} />
            </div>

            {/* Premium Intelligence entry */}
            <button
                type="button"
                onClick={() => setView("premium")}
                className="w-full flex items-center gap-3 rounded-xl bg-gradient-to-l from-amber-500/15 to-amber-500/5 border border-amber-500/30 p-3 active:from-amber-500/25 hover:from-amber-500/20 transition-colors touch-manipulation select-none"
                style={{ WebkitTapHighlightColor: 'transparent' }}
            >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/20 shrink-0">
                    <Gem className="h-5 w-5 text-amber-400" />
                </div>
                <div className="min-w-0 text-right flex-1">
                    <p className="text-sm font-bold text-amber-200">ذكاء البريميوم</p>
                    <p className="text-[10px] text-muted-foreground">دفعات الشراء · نسبة التفعيل · الجمهور النشط</p>
                </div>
                <ChevronLeft className="h-4 w-4 text-amber-400/70 shrink-0" />
            </button>

            {/* Channels */}
            <Card className="bg-card/60 border-border/40">
                <CardHeader className="pb-2 pt-3 px-3"><CardTitle className="text-sm flex items-center gap-2"><Radio className="h-4 w-4 text-cyan-400" /> القنوات والمجموعات</CardTitle></CardHeader>
                <CardContent className="px-3 pb-3 space-y-1.5">
                    {channels.sort((a, b) => (b.member_count || 0) - (a.member_count || 0)).map(ch => (
                        <button type="button" key={ch.chat_id} onClick={() => { setSelectedChannel(ch); setView("channel"); }} className="w-full text-right flex items-center justify-between rounded-lg bg-card/80 border border-border/30 p-2.5 active:bg-card hover:bg-card/60 transition-colors touch-manipulation select-none" style={{ WebkitTapHighlightColor: 'transparent' }}>
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{ch.title}</p>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {ch.username && <span className="text-[10px] text-muted-foreground font-mono" dir="ltr">@{ch.username}</span>}
                                    <Badge variant="outline" className={`text-[9px] px-1 py-0 ${ch.chat_type === "channel" ? "border-cyan-500/20 text-cyan-300" : "border-violet-500/20 text-violet-300"}`}>{ch.chat_type === "channel" ? "قناة" : "مجموعة"}</Badge>
                                    {ch.admin_account_username ? (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-500/20 text-emerald-300" dir="ltr">
                                            <Shield className="h-2 w-2 mr-0.5" />@{ch.admin_account_username}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-500/30 text-amber-300">
                                            <AlertCircle className="h-2 w-2 mr-0.5" />بدون مالك متصل
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-bold" dir="ltr">{(ch.member_count || 0).toLocaleString()}</span>
                                <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                            </div>
                        </button>
                    ))}
                </CardContent>
            </Card>

            {/* Events */}
            <Card className="bg-card/60 border-border/40">
                <CardHeader className="pb-2 pt-3 px-3"><CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4 text-cyan-400" /> آخر الأحداث</CardTitle></CardHeader>
                <CardContent className="px-3 pb-3 space-y-1">
                    {events.slice(0, 8).map((e, i) => {
                        const sc = { critical: "bg-rose-400", warning: "bg-amber-400", info: "bg-cyan-400", safe: "bg-emerald-400" };
                        return (
                            <div key={e.id || i} className="flex items-start gap-2 py-1.5 border-b border-border/20 last:border-0">
                                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${sc[e.severity] || "bg-cyan-400"}`} />
                                <div className="min-w-0 text-xs">
                                    <span className="font-mono text-muted-foreground">{e.event_type}</span>
                                    <p className="text-muted-foreground truncate">{e.reason}</p>
                                </div>
                            </div>
                        );
                    })}
                </CardContent>
            </Card>
        </div>
    );
}

/* ━━━━ Main ━━━━ */
export default function WebApp() {
    const { authed, user, error, loading } = useTelegramAuth();
    const [showConnect, setShowConnect] = useState(false);

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-[#0a0e14]">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        </div>
    );

    if (error) {
        const msg = { not_telegram: "افتح من داخل تلغرام", no_init_data: "لم يتم استلام بيانات المصادقة", not_allowed: "غير مصرّح — تواصل مع المالك", invalid_data: "بيانات غير صالحة", auth_failed: "فشل التحقق" };
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#0a0e14] p-6">
                <Card className="bg-card/60 border-border/40 max-w-sm w-full">
                    <CardContent className="p-6 text-center">
                        <Shield className="h-12 w-12 mx-auto text-rose-400 mb-3" />
                        <p className="text-sm text-muted-foreground">{msg[error] || error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (authed && showConnect) return (
        <div className="min-h-screen bg-[#0a0e14] text-foreground" dir="rtl">
            <PhoneLogin telegramUserId={user?.id} onSuccess={() => setShowConnect(false)} />
        </div>
    );

    if (authed) return (
        <div className="min-h-screen bg-[#0a0e14] text-foreground" dir="rtl">
            <MiniDashboard user={user} onShowConnect={() => setShowConnect(true)} onDisconnect={() => { setToken(null); window.location.reload(); }} />
        </div>
    );

    return null;
}
